import React, { useEffect, useReducer, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import client, { makePost } from "@culturehq/client";
import cloneDeep from "lodash/cloneDeep";
import { Icon, Loader, PlainButton } from "@culturehq/components";

import { useClickTransport, useProfile } from "app/context";
import { getName, getQueryStringKey, seo, t, Trans } from "app/utils";
import { QueryStringConsumer, QueryStringProvider, useQueryString } from "app/context/QueryStringContext";
import tagsTypes from "constants/tagsTypes";

import ActiveFeedItemTypeFilter from "components/feed/ActiveFeedItemTypeFilter";
import ErrorBoundary from "components/common/ErrorBoundary";
import ErrorMessage from "components/common/ErrorMessage";
import Featured from "components/Featured";

import FeedItemPlaceholder from "components/feed/FeedItemPlaceholder";
import FeedItem from "components/feed/FeedItem";
import FeedUpdates from "components/feed/FeedUpdates";
import SuggestedActions from "components/feed/SuggestedActions";
import FeedLeftInfo from "components/feed/FeedLeftInfo";
import TrendingStories from "components/stories/TrendingStories";
import LightboxStories from "./common/gallery/LightboxStories";
import PostStory from "./stories/post-stories-v2/PostStory";
import LightboxModal from "./common/gallery/LightboxModal";
import SelectTags from "./post/SelectTags";
import { useTagsFromQuery } from "./post/useTags";
import ExploreSuggestedActions from "./feed/ExploreSuggestedActions";
import SuggestedInterests from "./community/panels/SuggestedInterests";
import GettingStarted from "./feed/GettingStarted";
import PendingPrompts from "./feed/PendingPrompts";

const queryToOptions = queryString => {
  const { i, d, l, s, cl, cy, p, de, v, co } = queryString;

  return {
    interestIds: i || null,
    companyIds: co || null,
    departmentIds: d || null,
    locationIds: l || null,
    skillIds: s || null,
    clubIds: cl || null,
    classYearIds: cy || null,
    programIds: p || null,
    degreeIds: de || null,
    organizationValueIds: v || null
  };
};

const queryStringParsers = {
  i: "intList",
  d: "intList",
  l: "intList",
  s: "intList",
  cl: "intList",
  cy: "intList",
  p: "intList",
  de: "intList",
  v: "intList",
  co: "intList"
};

const initialState = {
  appending: null,
  error: null,
  fetching: false,
  pagination: {},
  options: queryToOptions({}),
  queryString: "",
  userActivities: []
};

const userActivityDeepUpdate = (currentUserActivities, userActivity) => {
  let newUserActivities = cloneDeep(currentUserActivities);
  let childIndex = -1;
  let trendSetterIndex = -1;

  const deepRemove = (() => {
    trendSetterIndex = newUserActivities.findIndex(ua => ua.id === userActivity.id);
    newUserActivities = newUserActivities.map(ua => {
      if (childIndex === -1) {
        childIndex = ua.childUserActivities.findIndex(cua => cua.id === userActivity.id);
      }
      const childUserActivities = ua.childUserActivities.filter(cua => cua.id !== userActivity.id);
      return { ...ua, childUserActivities };
    });
  })();

  const deepAdd = (() => {
    const parentIdx = newUserActivities.findIndex(ua => ua.id === userActivity.parentUserActivity);
    if (parentIdx === -1) {
      if (trendSetterIndex !== -1) {
        newUserActivities[trendSetterIndex] = userActivity;
      } else {
        newUserActivities.push(userActivity);
      }
    } else if (childIndex === -1) {
      newUserActivities[parentIdx].childUserActivities.push(userActivity);
    } else {
      newUserActivities[parentIdx].childUserActivities.splice(childIndex, 0, userActivity);
    }
  })();

  return newUserActivities;
};

const reducer = (state, action) => {
  switch (action.type) {
    case "appendStart":
      return { ...state, appending: state.pagination.currentPage + 1 };
    case "appendSuccess":
      return {
        ...state,
        appending: null,
        pagination: action.response.pagination,
        userActivities: [...state.userActivities, ...action.response.userActivities]
      };
    case "appendError":
      return { ...state, appending: null, error: action.error };
    case "fetchStart":
      return { ...state, error: null, fetching: true };
    case "fetchSuccess":
      return {
        ...state,
        error: null,
        fetching: false,
        pagination: action.response.pagination,
        userActivities: action.response.userActivities
      };
    case "fetchError":
      return { ...state, error: action.error, fetching: false };
    case "updateOptions":
      return {
        ...state,
        options: queryToOptions(action.queryString),
        queryString: action.queryString
      };
    case "userActivityUpdate":
      return {
        ...state,
        userActivities: state.userActivities.map(userActivity => {
          if (userActivity.id === action.userActivityId) {
            return { ...userActivity, ...action.updates };
          }
          return userActivity;
        })
      };
    case "userActivityRemove":
      return {
        ...state,
        userActivities: state.userActivities.filter(userActivity => (
          userActivity.id !== action.userActivityId
        ))
      };
    case "userActivityDeepUpdate":
      return {
        ...state,
        userActivities: userActivityDeepUpdate(state.userActivities, action.userActivity)
      };
    default:
      return state;
  }
};

const useFetchUserActivities = (
  dispatch,
  types,
  forYou,
  filterClicked,
  options,
  { organization: { allActivityDefault } }
) => useEffect(
  () => {
    let cancelled = false;
    dispatch({ type: "fetchStart" });

    client.listUserActivities({
      types, for_you: forYou && !allActivityDefault, ...options
    })
      .then(response => {
        if (!cancelled) {
          dispatch({ type: "fetchSuccess", response });
        }
      })
      .catch(error => {
        if (!cancelled) {
          dispatch({ type: "fetchError", error });
        }
      });

    return () => {
      cancelled = true;
    };
  },
  [dispatch, types, forYou, filterClicked, options, allActivityDefault]
);

const useAppendUserActivities = (
  dispatch, types, appending, forYou, options, { organization: { allActivityDefault } }
) => useEffect(
  () => {
    if (appending) {
      let cancelled = false;

      client.listUserActivities({
        types, page: appending, for_you: forYou && !allActivityDefault, ...options
      })
        .then(response => {
          if (!cancelled) {
            dispatch({ type: "appendSuccess", response });
          }
        })
        .catch(error => {
          if (!cancelled) {
            dispatch({ type: "appendError", error });
          }
        });

      return () => {
        cancelled = true;
      };
    }

    dispatch({ type: "appendError" });

    return undefined;
  },
  [dispatch, types, appending, forYou, options, allActivityDefault]
);

const useScrollListen = (dispatch, feedRef, pagination) => useEffect(
  () => {
    if (pagination.currentPage < pagination.totalPages && pagination.totalPages !== 0) {
      const handleScroll = () => {
        const feedBottom = feedRef.current.offsetTop + feedRef.current.offsetHeight;
        const windowBottom = window.pageYOffset + window.innerHeight;

        if ((feedBottom - windowBottom) < 1000) {
          dispatch({ type: "appendStart" });
        }
      };

      document.addEventListener("scroll", handleScroll);
      return () => document.removeEventListener("scroll", handleScroll);
    }

    return undefined;
  },
  [dispatch, feedRef, pagination]
);

const FeedItems = ({
  feedRef,
  state,
  dispatch,
  onFilterClear,
  types,
  filterClicked,
  forYou,
  queryString,
  profile,
  onFilterUsers,
  onUserActivityUpdate,
  onUserActivityRemove
}) => {
  const { onUsersClicked } = useClickTransport();

  useEffect(
    () => {
      seo({
        title: t("Home - CultureHQ.com")
      });
    },
    []
  );

  useEffect(
    () => {
      if (Object.entries(queryString).length !== 0
        || Object.values(state.options).filter(value => value !== null).length !== 0) {
        dispatch({ type: "updateOptions", queryString });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryString]
  );

  useEffect(
    () => {
      const getUsers = async () => {
        const { users, pagination } = await client.listUsers({ ...state.options, or: true });
        onFilterUsers({ users, options: state.options, pagination });
        onUsersClicked(users, pagination, state.options);
      };

      getUsers();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.options, onFilterUsers]
  );

  useFetchUserActivities(dispatch, types, forYou, filterClicked, state.options, profile);
  useAppendUserActivities(dispatch, types, state.appending, forYou, state.options, profile);
  useScrollListen(dispatch, feedRef, state.pagination);

  if (state.fetching) {
    return (
      <>
        <FeedItemPlaceholder />
        <FeedItemPlaceholder />
        <FeedItemPlaceholder />
      </>
    );
  }

  if (state.error) {
    return <ErrorMessage error={state.error} />;
  }


  const handleNotificationClicked = activities => {
    activities.forEach(activity => {
      if (activity.childUserActivities.length > 0) {
        dispatch({ type: "userActivityRemove", userActivityId: activity.id });
      }
      if (activity.activityType === "social_share") {
        const approvedContentRelated = state.userActivities.find(userActivity => userActivity.activityType === "approved_for_social" && userActivity.entity.entity.id === activity.entity.entity.id);
        if (approvedContentRelated) {
          dispatch({ type: "userActivityRemove", userActivityId: approvedContentRelated.id });
        }
      }
    });
  };

  return (
    <>
      <FeedUpdates forYou={forYou} onNotificationClicked={handleNotificationClicked} />
      {state.userActivities.map(userActivity => (
        <ErrorBoundary key={userActivity.id}>
          {userActivity.activityType !== "endorsement_created" && userActivity.activityType !== "disabled_story_created" && (
            <FeedItem
              item={userActivity}
              onUserActivityUpdate={onUserActivityUpdate}
              onUserActivityRemove={onUserActivityRemove}
            />
          )}
        </ErrorBoundary>
      ))}
      {state.appending && (
        <Loader loading />
      )}
      {state.userActivities.length === 0 && forYou && (
        <div className="EmptyResults EmptyResults-feed">
          <h2><Trans>No feed items to show...</Trans></h2>
          {types && (
            <PlainButton onClick={onFilterClear}>
              <Trans>Start over</Trans>
            </PlainButton>
          )}
        </div>
      )}
    </>
  );
};

const FeedContext = ({ queryString }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { onValuesClicked } = useClickTransport();
  const { profile, fetchedProfile } = useProfile();
  const location = useLocation();
  const { onClearQueryString, onToggleQueryStringValue } = useQueryString();
  const [types, setTypes] = useState(null);
  const [forYou, setForYou] = useState(true);
  const [filterClicked, setFilterClicked] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [activeStory, setActiveStory] = useState(null);
  const [activePhoto, setActivePhoto] = useState(null);
  const [modalPhotoIsOpen, setModalPhotoIsOpen] = useState(false);
  const [activeStoryQuestion, setActiveStoryQuestion] = useState(null);
  const [openShareStoryModal, setOpenShareStoryModal] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState({ users: [], options: {}, pagination: {} });
  const selectedTags = useTagsFromQuery(queryString);

  useEffect(
    () => {
      const getArrayDifferences = (array1, array2) => (
        array1.filter(option => (
          !array2.some(tag => tag.value === option.value)
        ))
      );
      const differences = getArrayDifferences(selectedOptions, selectedTags);

      if ((selectedTags.length !== 0 && selectedTags.length !== selectedOptions.length)
        || differences.length > 0) {
        setSelectedOptions(selectedTags);
        onValuesClicked(selectedTags.filter(option => option.category === tagsTypes.ORG_VALUES));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryString, selectedTags]
  );

  useEffect(
    () => {
      if (Object.keys(queryString).length !== 0) {
        setForYou(false);
      }
    },
    [queryString]
  );

  const handleFilterClear = () => setTypes(null);

  const handleTypeToggle = type => setTypes(current => {
    if (current && current.includes(type)) {
      const results = current.filter(activeType => type !== activeType);
      return results.length ? results : null;
    }
    return [...(types || []), type];
  });

  const handleClose = () => {
    setModalIsOpen(false);
  };

  const feedRef = useRef();

  const handleForYouFilterSelected = (value, isFilterClick = false) => {
    setSelectedOptions([]);
    onValuesClicked([]);
    setForYou(value);
    setFilterClicked(isFilterClick);
  };

  useEffect(
    () => {
      const getStory = async storyId => {
        const { story } = await client.getStory({ storyId });
        setActiveStory(story);
        setModalIsOpen(true);
      };

      const getStoryQuestion = async storyQuestionId => {
        const { storyQuestion } = await client.getStoryQuestion({ storyQuestionId });
        setActiveStoryQuestion(storyQuestion);
      };

      const getPhoto = async photoId => {
        const { photo } = await client.getPhoto({ photoId });
        setActivePhoto(photo);
        setModalPhotoIsOpen(true);
      };

      const newAction = path => {
        switch (path) {
          case "create":
            setOpenShareStoryModal(true);
            break;
          default:
            getStoryQuestion(path);
            break;
        }
      };

      const paths = location.pathname.split("/");
      switch (paths[1]) {
        case "photos":
          getPhoto(paths[2]);
          break;
        case "stories":
          if (paths[3] !== "engage" && paths[3] !== "share") {
            getStory(paths[2]);
          }
          break;
        case "new":
          newAction(paths[3]);
          break;
        default:
          setOpenShareStoryModal(null);
          setActiveStoryQuestion(null);
          break;
      }
    },
    [location.pathname]
  );

  const handleStoryCommentChange = (_, numberOfComments) => {
    setActiveStory({ ...activeStory, commentCount: numberOfComments });
  };

  const getArrayDifferences = (array1, array2) => (
    array1.filter(option => (
      !array2.some(tag => tag.value === option.value)
    ))
  );

  const handleSelectedTags = (updatedOptions, _) => {
    if (updatedOptions.length === 0) {
      onClearQueryString();
      setSelectedOptions([]);
      onValuesClicked([]);
      return;
    }

    const deletedOptions = getArrayDifferences(selectedOptions, updatedOptions);
    deletedOptions.forEach(deletedOption => {
      onToggleQueryStringValue(getQueryStringKey(deletedOption.category), deletedOption.value);
    });

    const addedOptions = getArrayDifferences(updatedOptions, selectedOptions);
    addedOptions.forEach(addedOption => {
      onToggleQueryStringValue(getQueryStringKey(addedOption.category), addedOption.value);
    });


    onValuesClicked(updatedOptions.filter(option => option.category === tagsTypes.ORG_VALUES));
    setSelectedOptions(updatedOptions);
  };

  const createSelectedOptionFromInterest = interest => (
    { value: interest.id, category: "interests", label: interest.name, categoryIcon: "heart-ol" }
  );

  const handleInterestClicked = interest => {
    const tags = selectedOptions.filter(tag => tag.value !== interest.id);
    if (tags.length !== selectedOptions.length) {
      setSelectedOptions(tags);
    } else {
      setSelectedOptions([...selectedOptions, createSelectedOptionFromInterest(interest)]);
    }
  };

  const addInterest = async interestName => {
    const { userInterest } = await client.createProfileUserInterest({ name: interestName });
    fetchedProfile({
      ...profile,
      interests: [...profile.interests, userInterest.interest]
    });
  };

  const addSkill = async skillName => {
    const json = await makePost("/profile/user_skills", { name: skillName });
    fetchedProfile({
      ...profile,
      skills: [...profile.skills, json.userSkill.skill]
    });
  };

  const actionCallback = (_, category, label) => {
    switch (category) {
      case "interests":
        addInterest(label);
        break;
      case "skills":
        addSkill(label);
        break;
      case "departments":
      case "locations":
      default:
        break;
    }
  };

  const handleClearSelectedOptions = () => {
    onClearQueryString();
    setSelectedOptions([]);
    onValuesClicked([]);
  };

  const handleGetUsers = async page => {
    const { users, pagination } = await client.listUsers(
      { page, ...filteredUsers.options, or: true }
    );
    setFilteredUsers({ users, pagination, options: filteredUsers.options });
  };

  const handleUserActivityUpdate = ({ userActivityId, updates, userActivity }) => {
    if (userActivity) {
      dispatch({ type: "userActivityDeepUpdate", userActivity });
      return;
    }
    dispatch({ type: "userActivityUpdate", userActivityId, updates });
  };

  const handleUserActivityRemove = userActivityId => (
    dispatch({ type: "userActivityRemove", userActivityId })
  );

  return (
    <div className="Feed Container-left_nav" ref={feedRef}>
      <section role="complementary" aria-label={t("Left bar")}>
        <div className="Filters Left-nav no-scrollbar Feed--column SidePanel col-xs-12 col-md-8">
          <FeedLeftInfo
            forYou={forYou}
            types={types}
            onFilterClear={handleFilterClear}
            onForYouFilterSelected={handleForYouFilterSelected}
            onInterestClicked={handleInterestClicked}
            onTypeToggle={handleTypeToggle}
          />
        </div>
      </section>
      <div className="Feed--column Container-left_nav--main-column col-xs-12">
        <section role="main">
          <div className="Feed--column Central--column col-xs-12 col-md-8">
            {activeStoryQuestion && (
              <PostStory
                initialOpen
                preFilledQuestion={activeStoryQuestion}
              />
            )}
            {openShareStoryModal && (
              <PostStory
                initialOpen
                queryString={queryString}
              />
            )}
            {activeStory && (
              <LightboxStories
                modalIsOpen={modalIsOpen}
                activeStory={activeStory}
                currentUserAnswered
                stories={[activeStory]}
                onClose={handleClose}
                onCommentsChange={handleStoryCommentChange}
                onDelete={() => {}}
                onStoryCheerToggle={() => {}}
                onStoryUpdate={() => {}}
              />
            )}
            {activePhoto && (
              <LightboxModal
                activePhoto={activePhoto}
                photos={[activePhoto]}
                modalIsOpen={modalPhotoIsOpen}
                onClose={() => setModalPhotoIsOpen(false)}
                onPhotoChange={() => {}}
                onDelete={() => {}}
                onPhotoCheerToggle={() => {}}
                onPhotoUpdate={() => {}}
              />
            )}
            <PendingPrompts />
            {!forYou && (
              <ExploreSuggestedActions
                selectedOptions={selectedOptions}
                filteredUsers={filteredUsers}
                onGetUsers={handleGetUsers}
              >
                <SelectTags
                  ariaLabel={t("Search and explore content by tags")}
                  placeholder={t(`Hi ${getName(profile)}, search and explore content by tags`)}
                  selectedOptions={selectedOptions}
                  onShareWithChanged={handleSelectedTags}
                  isSearchBar
                  optionsCallback={actionCallback}
                  onClearSelectedOptions={handleClearSelectedOptions}
                  withOrgValues
                />
                {selectedOptions.length > 0 && <PlainButton aria-label={t("Clear button")} className="clear-btn" onClick={handleClearSelectedOptions}><Icon icon="android-close" /></PlainButton>}
              </ExploreSuggestedActions>
            )}
            {forYou && <SuggestedActions />}
            <TrendingStories
              forYou={forYou}
              queryString={queryString}
              selectedOptions={selectedOptions}
            />
            <ErrorBoundary>
              {types && (
                <div className="Filters__Active">
                  <h2 className="Filters__Active--title"><Trans>Applied Filters:</Trans></h2>
                  {types.map(type => (
                    <ActiveFeedItemTypeFilter
                      key={type}
                      type={type}
                      onTypeToggle={handleTypeToggle}
                    />
                  ))}
                </div>
              )}
              <section role="feed" aria-label={t("Feed section")}>
                <h2 className="title-hidden"><Trans>News section posts</Trans></h2>
                <FeedItems
                  feedRef={feedRef}
                  state={state}
                  dispatch={dispatch}
                  onFilterClear={handleFilterClear}
                  types={types}
                  filterClicked={filterClicked}
                  forYou={forYou}
                  profile={profile}
                  queryString={queryString}
                  onFilterUsers={setFilteredUsers}
                  onUserActivityRemove={handleUserActivityRemove}
                  onUserActivityUpdate={handleUserActivityUpdate}
                />
              </section>
            </ErrorBoundary>
          </div>
        </section>
        <section role="complementary" aria-label={t("Right bar")}>
          <div className="Feed--right Feed--column hidden-xs hidden-sm col-md-4">
            {forYou && <Featured forYou={forYou} queryString={queryString} />}
            {!forYou && (
              <>
                <GettingStarted />
                <Featured forYou={forYou} queryString={queryString} />
                <SuggestedInterests isFeed />
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

const Feed = props => (
  <QueryStringProvider parsers={queryStringParsers}>
    <QueryStringConsumer>
      {queryStringContext => (
        <FeedContext
          {...props}
          {...queryStringContext}
        />
      )}
    </QueryStringConsumer>
  </QueryStringProvider>
);


export default Feed;
